import React from "react";

import { formatDateUS } from "@/utils/dateUtils";

// Define a type for the organization data
export interface OrganizationDetailsData {
  id: string;
  name: string;
  domain: string | null;
  contactEmail: string;
  type: string;
  status: string;
  website_url: string | null;
  linkedIn_url: string | null;
  planMaster?: {
    id: string;
    name: string;
    displayName: string;
    description: string;
    price: number;
    licenselimit: number;
    tokenlimit: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    planMasterId: string;
  };
  tenantProfile?: {
    about: string;
    competitors: string[];
    partners: string[];
    productsServices: string[];
    salesMethodology: string[];
    targetIndustries: string[];
    uniqueSellingPoints: string[];
    valueProposition: string[];
    createdAt: string;
    updatedAt: string;
    id: string;
    tenantId: string;
    updatedby: string | null;
  };
  usage_log?: unknown[];
  subscriptions?: any[];
  expiryDate?: string;
  createdAt: string;
  updatedAt: string;
}

type Props = {
  open: boolean;
  onClose: () => void;
  data: OrganizationDetailsData;
};

const OrganizationDetailsDialog: React.FC<Props> = ({
  open,
  onClose,
  data,
}) => {
  if (!open) return null;

  const activeSubscription = Array.isArray(data.subscriptions) && data.subscriptions.length > 0
    ? data.subscriptions.find((s: any) => s.status === "ACTIVE") || data.subscriptions[0]
    : null;

  const effectiveExpiryDate = activeSubscription?.endDate || data.expiryDate;
  const subscriptionStartDate = activeSubscription?.startDate || activeSubscription?.createdAt || data.createdAt;
  const licenseCount = activeSubscription?.licenseCount || data.planMaster?.licenselimit || 5;
  const subscriptionStatus = activeSubscription?.status || data.status || "ACTIVE";

  return (
    <div className={`fixed inset-0 z-[300] flex items-center justify-center backdrop-blur-sm bg-black/40 ${localStorage.getItem("theme") === "light" ? "light-theme-dashboard" : ""}`}>
      <div className="relative w-[95%] max-w-6xl max-h-screen overflow-y-auto bg-[var(--color-surface)] text-[var(--color-text)] border border-[var(--color-border)] rounded-lg shadow-lg p-6">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-[var(--color-text)] opacity-70 hover:opacity-100 hover:text-red-400 cursor-pointer text-3xl"
        >
          ×
        </button>

        <h2 className="text-2xl font-semibold mb-6 text-center text-[var(--color-text)]">
          Organization & Plan Details
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Card - Organization Info */}
          <div className="bg-[var(--color-bg)] rounded-lg p-4 shadow-md border border-[var(--color-border)]">
            <Section title="Organization Info">
              <DetailItem label="Name" value={data.name} />
              <DetailItem label="Email" value={data.contactEmail} />
              <DetailItem label="Type" value={data.type} />
              <DetailItem
                label="Status"
                value={
                  typeof data?.status === "string" && data.status.trim()
                    ? data.status.charAt(0).toUpperCase() +
                      data.status.slice(1).toLowerCase()
                    : "Not found"
                }
              />
              <DetailItem
                label="Created At"
                value={formatDateUS(data.createdAt, true)}
              />
              <DetailItem
                label="Updated At"
                value={formatDateUS(data.updatedAt, true)}
              />

              <DetailItem
                label="Website"
                value={
                  data.website_url?.trim() ? (
                    <a
                      href={data.website_url}
                      className="text-blue-400 underline truncate block max-w-[320px]"
                      target="_blank"
                      rel="noopener noreferrer"
                      title={data.website_url}
                    >
                      {data.website_url}
                    </a>
                  ) : (
                    <span className="truncate block max-w-[320px]">
                      Not Provided
                    </span>
                  )
                }
              />

              <DetailItem
                label="LinkedIn"
                value={
                  data.linkedIn_url?.trim() ? (
                    <a
                      href={data.linkedIn_url}
                      className="text-blue-400 underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {data.linkedIn_url}
                    </a>
                  ) : (
                    "Not Provided"
                  )
                }
              />
            </Section>
          </div>

          {/* Right Card - Plan Info */}
          <div className="bg-[var(--color-bg)] rounded-lg p-4 shadow-md border border-[var(--color-border)]">
            <Section title="Plan Info">
              {/* Plan Details */}
              {data.planMaster ? (
                <>
                  <DetailItem
                    label="Plan Name"
                    value={data.planMaster.displayName || data.planMaster.name}
                  />
                  <DetailItem label="Plan Type" value={data.planMaster.name} />

                  <DetailItem
                    label="Status"
                    value={
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                        {typeof subscriptionStatus === "string" ? subscriptionStatus.toUpperCase() : "ACTIVE"}
                      </span>
                    }
                  />

                  <DetailItem
                    label="Licenses"
                    value={`${licenseCount} Licenses`}
                  />

                  <DetailItem
                    label="Subscribed On"
                    value={formatDateUS(subscriptionStartDate, true)}
                  />

                  {effectiveExpiryDate && effectiveExpiryDate !== "" && effectiveExpiryDate !== "N/A" ? (
                    <DetailItem
                      label="Expiry Date"
                      value={formatDateUS(effectiveExpiryDate, true)}
                    />
                  ) : null}

                  <DetailItem
                    label="Price"
                    value={`$${data.planMaster.price}`}
                  />
                  <DetailItem
                    label="Description"
                    value={data.planMaster.description || "Starter Plan"}
                    isFull
                  />
                </>
              ) : (
                <p className="text-sm text-[var(--color-muted)]">No plan information available.</p>
              )}
            </Section>
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="mb-2">
    <h3 className="text-lg font-bold mb-2 text-[var(--color-text)]">{title}</h3>
    <div className="border-b border-[var(--color-border)] mb-4" />
    <div className="space-y-3">{children}</div>
  </div>
);

const DetailItem = ({
  label,
  value,
  isFull = false,
}: {
  label: string;
  value: React.ReactNode;
  isFull?: boolean;
}) => (
  <div
    className={`flex ${isFull ? "flex-col" : "justify-between"} gap-2 text-sm sm:text-base`}
  >
    <span className="font-semibold text-[var(--color-muted)] min-w-[120px]">{label}</span>
    <span
      className={`${isFull ? "text-left mt-1" : "text-right"} text-[var(--color-text)] truncate`}
    >
      {value}
    </span>
  </div>
);

export default OrganizationDetailsDialog;
